// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, StatusBanner } from '@loa/ui-components';
import type { AnnotationBinding, BindingCatalogue, NewBusinessTemplate } from './types';
import type { NewBusinessTemplateEditorApi } from './adapter';
import PdfAnnotationEditor from './PdfAnnotationEditor';
import NewBusinessParamSchemaEditor from './NewBusinessParamSchemaEditor';
import NewBusinessParamSchemaBuilder from './NewBusinessParamSchemaBuilder';
import NewBusinessBindingsJsonEditor from './NewBusinessBindingsJsonEditor';

export interface NewBusinessTemplateEditorProps {
  /** The template being edited (must carry an `id`). */
  template: NewBusinessTemplate;
  /** Transport/scope-specific operations (see {@link NewBusinessTemplateEditorApi}). */
  api: NewBusinessTemplateEditorApi;
  /**
   * Show the raw JSON editors (parameter schema + bindings JSON). Admin-only —
   * the Advisor UI hides them, keeping only the visual PDF binding editor.
   */
  showJsonEditors?: boolean;
  /** Called after any successful mutation (upload / save / activate) with the fresh row. */
  onSaved?: (template: NewBusinessTemplate) => void;
  /** Called when the author closes the editor. */
  onClose?: () => void;
}

const headerStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-h4)',
  fontWeight: 'var(--font-weight-semibold)' as unknown as number,
  margin: 'var(--spacing-lg) 0 var(--spacing-sm)',
};

const helpTextStyle: React.CSSProperties = {
  color: 'var(--color-secondary-text)',
  fontSize: 'var(--font-size-body-03)',
  margin: '0 0 var(--spacing-sm)',
};

/**
 * The shared New Business template authoring editor, used by both the Admin UI
 * (platform-scope, JSON editors on) and the Advisor UI (brokerage-override
 * scope, JSON editors off). Renders the numbered steps — PDF upload → visual
 * bindings → (admin) parameter schema + bindings JSON — plus Save and Activate,
 * driven entirely through the injected {@link NewBusinessTemplateEditorApi}.
 */
export default function NewBusinessTemplateEditor({
  template: initial,
  api,
  showJsonEditors = false,
  onSaved,
  onClose,
}: NewBusinessTemplateEditorProps) {
  const [template, setTemplate] = useState<NewBusinessTemplate>(initial);
  const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null);
  const [bindings, setBindings] = useState<AnnotationBinding[]>(initial.annotationMap ?? []);
  const [paramSchema, setParamSchema] = useState<Record<string, unknown>>(initial.paramSchema ?? {});
  const [catalogue, setCatalogue] = useState<BindingCatalogue | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rawSchema, setRawSchema] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const templateId = initial.id;

  // Reset working state whenever a different template is opened.
  useEffect(() => {
    setTemplate(initial);
    setBindings(initial.annotationMap ?? []);
    setParamSchema(initial.paramSchema ?? {});
    setPdfData(null);
    setError(null);
    if (initial.hasPdf && initial.id) {
      api.fetchPdf(initial.id)
        .then(setPdfData)
        .catch(() => { /* the editor shows the upload prompt */ });
    }
    // Re-run only when the opened template changes, not on every api identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial.id]);

  useEffect(() => {
    api.bindingCatalogue()
      .then(setCatalogue)
      .catch(() => { /* catalogue is optional for placing signature-only forms */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onUploadFile = useCallback(async (file: File) => {
    if (!templateId) return;
    if (file.type !== 'application/pdf') {
      setError('Only PDF files are allowed');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await api.uploadPdf(templateId, file);
      setTemplate(updated);
      setPdfData(await api.fetchPdf(templateId));
      onSaved?.(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setSaving(false);
    }
  }, [api, onSaved, templateId]);

  const save = useCallback(async () => {
    if (!templateId) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await api.update(templateId, {
        ...template,
        annotationMap: bindings,
        paramSchema,
      });
      setTemplate(updated);
      onSaved?.(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [api, bindings, onSaved, paramSchema, template, templateId]);

  const activate = useCallback(async () => {
    if (!templateId) return;
    setError(null);
    try {
      // Publish is gated (≥1 signature region + PDF); a violation surfaces as 422.
      const updated = await api.activate(templateId);
      setTemplate(updated);
      onSaved?.(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not activate — a PDF and at least one signature region are required.');
    }
  }, [api, onSaved, templateId]);

  return (
    <div data-testid="nb-editor">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 style={{ fontSize: 'var(--font-size-h3)', margin: 0 }}>
          Editing: {template.productName ?? template.productId}
        </h3>
        {onClose && (
          <Button variant="link" size="sm" onClick={onClose} data-testid="nb-editor-close">
            Close
          </Button>
        )}
      </div>

      {error && (
        <div style={{ marginTop: 'var(--spacing-sm)' }}>
          <StatusBanner
            variant="error"
            position="inline"
            visible
            message={error}
            onDismiss={() => setError(null)}
          />
        </div>
      )}

      <h4 style={headerStyle}>1 · Provider form PDF</h4>
      <Button
        variant="outlined"
        size="sm"
        loading={saving}
        onClick={() => fileInputRef.current?.click()}
        data-testid="nb-upload-btn"
      >
        {template.hasPdf ? 'Replace PDF' : 'Upload PDF'}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        style={{ display: 'none' }}
        data-testid="nb-file-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onUploadFile(f);
        }}
      />

      <h4 style={headerStyle}>2 · Bindings</h4>
      <PdfAnnotationEditor pdfData={pdfData} bindings={bindings} catalogue={catalogue} paramSchema={paramSchema} onChange={setBindings} />

      <h4 style={headerStyle}>3 · Parameter schema</h4>
      <p style={helpTextStyle}>
        Extra questions the form asks the advisor or client for data the platform
        doesn&apos;t already hold (e.g. National Insurance number, payment details,
        fund choices), grouped into collapsible sections and laid out two-per-row
        (full-width for long text). Each field&apos;s answer becomes a{' '}
        <code>param.&lt;id&gt;</code> data source you can bind onto the form in step 2.
        Leave it empty if the form needs nothing beyond the known client, proposal
        and product data.
      </p>
      <NewBusinessParamSchemaBuilder schema={paramSchema} onChange={setParamSchema} catalogue={catalogue} />
      {showJsonEditors && (
        <div style={{ marginTop: 'var(--spacing-sm)' }}>
          <Button variant="link" size="sm" onClick={() => setRawSchema((v) => !v)} data-testid="nb-schema-raw-toggle">
            {rawSchema ? 'Hide raw JSON' : 'Advanced: edit as JSON'}
          </Button>
          {rawSchema && <NewBusinessParamSchemaEditor schema={paramSchema} onChange={setParamSchema} />}
        </div>
      )}

      {showJsonEditors && (
        <>
          <h4 style={headerStyle}>4 · Bindings (JSON)</h4>
          <p style={helpTextStyle}>
            The placed bindings as raw JSON — the same data as the visual editor in step 2.
            Edit page / x / y coordinates, field types or data sources directly, or paste a
            set of bindings; changes reflect in the overlay above. The annotation map
            (including the ≥1 signature-region rule) is re-validated on save.
          </p>
          <NewBusinessBindingsJsonEditor bindings={bindings} onChange={setBindings} />
        </>
      )}

      <div style={{ marginTop: 'var(--spacing-lg)', display: 'flex', gap: 'var(--spacing-sm)' }}>
        <Button variant="filled" colour="purple" loading={saving} onClick={save} data-testid="nb-save-editor">
          {showJsonEditors ? 'Save bindings & schema' : 'Save bindings'}
        </Button>
        <Button
          variant="filled"
          colour="green"
          disabled={template.isActive}
          onClick={activate}
          data-testid="nb-activate-editor"
        >
          {template.isActive ? 'Active' : 'Activate'}
        </Button>
      </div>
    </div>
  );
}
